<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAutomation } from 'dashboard/composables/useAutomation';
import AutomationRuleForm from './AutomationRuleForm.vue';
import { useI18n } from 'vue-i18n';
import {
  DATE_REACHED_EVENT,
  buildDateTriggerTemplate,
  buildDefaultDateTrigger,
} from './dateTrigger';

const emit = defineEmits(['saveAutomation']);

const START_VALUE = {
  name: null,
  description: null,
  event_name: 'conversation_created',
  execution_delay: null,
  execution_delay_trigger: null,
  conditions: [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ],
  actions: [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ],
};

const store = useStore();
const { t } = useI18n();
const formRef = ref(null);

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewCondition,
  appendNewAction,
  removeFilter,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation(START_VALUE);

const buildDateRule = templateKey => {
  const rule = {
    ...structuredClone(START_VALUE),
    event_name: DATE_REACHED_EVENT,
    conditions: [],
    date_trigger: buildDefaultDateTrigger(),
  };
  if (!templateKey) return rule;
  return {
    ...rule,
    ...buildDateTriggerTemplate(templateKey),
    name: t(
      `AUTOMATION.DATE_TRIGGER.TEMPLATES.${templateKey.toUpperCase()}.NAME`
    ),
  };
};

// startsOnDate abre direto em "Em uma data" (aba Em uma data da lista ou ?new=date);
// templateKey preenche com um modelo pronto (?template=birthday etc.).
const open = (
  executionDelay = null,
  { startsOnDate = false, templateKey = null } = {}
) => {
  automation.value = startsOnDate
    ? buildDateRule(templateKey)
    : structuredClone(START_VALUE);
  manifestCustomAttributes();
  formRef.value?.open(executionDelay, automation.value.date_trigger);
};
const close = () => formRef.value?.close();

const onSave = (payload, mode) => {
  emit('saveAutomation', payload, mode);
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
});

defineExpose({ open, close });
</script>

<template>
  <AutomationRuleForm
    ref="formRef"
    v-model:automation="automation"
    mode="create"
    :automation-types="automationTypes"
    :get-condition-dropdown-values="getConditionDropdownValues"
    :get-action-dropdown-values="getActionDropdownValues"
    :append-new-condition="appendNewCondition"
    :append-new-action="appendNewAction"
    :remove-filter="removeFilter"
    :remove-action="removeAction"
    :reset-action="resetAction"
    :on-event-change="onEventChange"
    @save="onSave"
  />
</template>
